import { useCallback, useEffect, useRef, useState } from "react";

const STORAGE_KEY = "0g-vibe:create-token-draft";

type WizardDraft = {
	step: number;
	formData: Record<string, unknown>;
	timestamp: number;
};

function hasKey<K extends string>(obj: object, key: K): obj is Record<K, unknown> {
	return key in obj;
}

function isWizardDraft(value: unknown): value is WizardDraft {
	if (typeof value !== "object" || value === null) {
		return false;
	}
	return (
		hasKey(value, "step") &&
		typeof value.step === "number" &&
		hasKey(value, "formData") &&
		typeof value.formData === "object" &&
		hasKey(value, "timestamp") &&
		typeof value.timestamp === "number"
	);
}

export function useWizardDraft() {
	const [hasDraft, setHasDraft] = useState(false);
	const [draftData, setDraftData] = useState<WizardDraft | null>(null);

	// Check for existing draft on mount
	useEffect(() => {
		const stored = sessionStorage.getItem(STORAGE_KEY);
		if (stored) {
			try {
				const parsed: unknown = JSON.parse(stored);
				if (isWizardDraft(parsed)) {
					setDraftData(parsed);
					setHasDraft(true);
				} else {
					sessionStorage.removeItem(STORAGE_KEY);
				}
			} catch {
				// Invalid JSON, clear it
				sessionStorage.removeItem(STORAGE_KEY);
			}
		}
	}, []);

	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Clean up debounce timer on unmount
	useEffect(() => {
		return () => {
			if (debounceRef.current) {
				clearTimeout(debounceRef.current);
			}
		};
	}, []);

	// Save draft to sessionStorage (debounced)
	const saveDraft = useCallback((step: number, formData: Record<string, unknown>) => {
		const draft: WizardDraft = {
			step,
			formData,
			timestamp: Date.now(),
		};

		// Cancel previous debounce timer
		if (debounceRef.current) {
			clearTimeout(debounceRef.current);
		}

		debounceRef.current = setTimeout(() => {
			sessionStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
			debounceRef.current = null;
		}, 500);
	}, []);

	// Clear draft from sessionStorage
	const clearDraft = useCallback(() => {
		sessionStorage.removeItem(STORAGE_KEY);
		setHasDraft(false);
		setDraftData(null);
	}, []);

	// Resume from draft
	const resumeDraft = useCallback(() => {
		return draftData;
	}, [draftData]);

	return {
		hasDraft,
		draftData,
		saveDraft,
		clearDraft,
		resumeDraft,
	};
}
